// src/components/sections/HowItWorksDemo.js
import React from 'react';
import Image from '../elements/Image';
import { withLanguage } from '../../context/LanguageContext';

const copy = (isEnglish, ja, en) => (isEnglish ? en : ja);

/* =====================================================
   Step meta (chips / navigation)
   ===================================================== */
const STEPS = [
  {
    key: 'a',
    icon: require('./../../assets/icons/New_auto_structuring_icon_1771221574617.png'),
    label: '自動構造化',
    caption: '最大5ページのPDF・画像をアップロードし、実行すると全件を自動抽出。',
    labelEn: 'Automatic structuring', captionEn: 'Upload a PDF or image of up to five pages and extract every field automatically.',
  },
  {
    key: 'b',
    icon: require('./../../assets/icons/New_importance_classification_icon_1771221592386.png'),
    label: '重要度提案',
    caption: '各項目に「コア / 管理用 / 補助用」を自動付与したJSONを生成。',
    labelEn: 'Importance suggestions', captionEn: 'Generate JSON with Core, Administrative, and Auxiliary importance assigned automatically.',
  },
  {
    key: 'c',
    icon: require('./../../assets/icons/New_icon_template_1771221484269.png'),
    label: 'テンプレート登録',
    caption: '重要度を調整し保存。カラム名のみ登録され実データは残りません。',
    labelEn: 'Save template', captionEn: 'Adjust importance and save only the column names—source data is never retained.',
  },
  {
    key: 'd',
    icon: require('./../../assets/icons/New_icon_automation_1771221530652.png'),
    label: 'ワンクリック化',
    caption: 'テンプレートを指定すれば、必要な重要度のデータだけを抽出。',
    labelEn: 'One-click extraction', captionEn: 'Select a template to extract only the data at the importance levels you need.',
  },
];

/* Sample rows from a general-purpose 請求書 (invoice) */
const ROWS = [
  { path: 'タイトル', value: '請求書', imp: 'admin' },
  { path: '請求書番号', value: 'INV-2024-0342', imp: 'admin' },
  { path: '発行日', value: '2024年9月5日', imp: 'admin' },
  { path: '請求先', value: '株式会社ABC商事', imp: 'core' },
  { path: '品目 / 名称', value: 'Webサイト制作一式', imp: 'core' },
  { path: '品目 / 数量', value: '1', imp: 'core' },
  { path: '品目 / 単価', value: '¥300,000', imp: 'core' },
  { path: '合計金額', value: '¥330,000', imp: 'core' },
];

const IMP_LABEL = { core: 'コア', admin: '管理用', aux: '補助用' };
const FIELD_EN = {
  'タイトル': 'Title', '請求書番号': 'Invoice number', '発行日': 'Issue date',
  '請求先': 'Bill to', '品目 / 名称': 'Item / Name', '品目 / 数量': 'Item / Quantity',
  '品目 / 単価': 'Item / Unit price', '合計金額': 'Total'
};
const VALUE_EN = { '請求書': 'Invoice', '2024年9月5日': 'September 5, 2024', '株式会社ABC商事': 'ABC Trading Co., Ltd.', 'Webサイト制作一式': 'Website development services' };

const AUTO_ADVANCE_MS = 6000;

const Badge = ({ imp, className = '', isEnglish = false }) => (
  <span className={`hiw-badge hiw-badge--${imp} ${className}`}>{isEnglish ? { core: 'Core', admin: 'Administrative', aux: 'Auxiliary' }[imp] : IMP_LABEL[imp]}</span>
);

/* Small window chrome wrapper */
const Window = ({ title, children }) => (
  <div className="hiw-window">
    <div className="hiw-window-bar">
      <span className="hiw-dot" />
      <span className="hiw-dot" />
      <span className="hiw-dot" />
      <span className="hiw-window-title">{title}</span>
    </div>
    <div className="hiw-window-body">{children}</div>
  </div>
);

/* =====================================================
   STEP 1 — Upload & extract everything
   ===================================================== */
const StageUpload = ({ isEnglish }) => (
  <Window title={copy(isEnglish, 'PITHIAS Extract — 抽出', 'PITHIAS Extract — Extraction')}>
    <div className="hiw-split">
      <div className="hiw-pane">
        <div className="hiw-pane-label">{copy(isEnglish, '1. 文書のアップロード', '1. Upload document')}</div>
        <div className="hiw-doc">
          <div className="hiw-doc-head">
            <span className="hiw-doc-title">{copy(isEnglish, '請求書', 'Invoice')}</span>
            <span className="hiw-doc-stamp">No. INV-2024-0342</span>
          </div>
          <span className="hiw-doc-line" style={{ width: '52%' }} />
          <span className="hiw-doc-line" style={{ width: '40%' }} />
          <div className="hiw-doc-invtable">
            <span /><span /><span />
            <span /><span /><span />
            <span /><span /><span />
          </div>
          <span className="hiw-doc-total" />
        </div>
        <div className="hiw-pagebar">Page 1 / 5</div>
        <button className="hiw-btn hiw-btn--extract hiw-press">
          {copy(isEnglish, '抽出を実行', 'Run extraction')}
          <span className="hiw-cursor" />
        </button>
      </div>

      <div className="hiw-pane">
        <div className="hiw-pane-label">{copy(isEnglish, '抽出結果のレビューと修正', 'Review and edit extraction results')}</div>
        <div className="hiw-table">
          <div className="hiw-thead">
            <span>{copy(isEnglish, '項目名', 'Field')}</span>
            <span>{copy(isEnglish, '抽出された値', 'Extracted value')}</span>
          </div>
          {ROWS.map((r, i) => (
            <div key={r.path} className="hiw-row hiw-row--stream" style={{ '--i': i }}>
              <span className="hiw-cell hiw-cell--path">{isEnglish ? FIELD_EN[r.path] || r.path : r.path}</span>
              <span className="hiw-cell hiw-cell--val">{isEnglish ? VALUE_EN[r.value] || r.value : r.value}</span>
            </div>
          ))}
        </div>
        <div className="hiw-count hiw-count--in">
          {copy(isEnglish, `全 ${ROWS.length} 項目を自動抽出 ✓`, `All ${ROWS.length} fields extracted automatically ✓`)}
        </div>
      </div>
    </div>
  </Window>
);

/* =====================================================
   STEP 2 — Importance auto-assigned
   ===================================================== */
const StageImportance = ({ isEnglish }) => (
  <Window title={copy(isEnglish, 'PITHIAS Extract — 重要度の自動付与', 'PITHIAS Extract — Automatic importance')}>
    <div className="hiw-single">
      <div className="hiw-ai-chip">🤖 {copy(isEnglish, '生成AIが重要度を自動判定', 'Generative AI assigns importance automatically')}</div>
      <div className="hiw-table">
        <div className="hiw-thead hiw-thead--3">
          <span>{copy(isEnglish, '項目名', 'Field')}</span>
          <span>{copy(isEnglish, '抽出された値', 'Extracted value')}</span>
          <span>{copy(isEnglish, '重要度', 'Importance')}</span>
        </div>
        {ROWS.map((r, i) => (
          <div key={r.path} className="hiw-row hiw-row--3" style={{ '--i': i }}>
            <span className="hiw-cell hiw-cell--path">{isEnglish ? FIELD_EN[r.path] || r.path : r.path}</span>
            <span className="hiw-cell hiw-cell--val">{isEnglish ? VALUE_EN[r.value] || r.value : r.value}</span>
            <span className="hiw-cell hiw-cell--imp">
              <Badge imp={r.imp} className="hiw-badge--pop" isEnglish={isEnglish} />
            </span>
          </div>
        ))}
      </div>
      <div className="hiw-legend">
        <Badge imp="core" isEnglish={isEnglish} /> {copy(isEnglish, '重要データ', 'Important data')}
        <Badge imp="admin" isEnglish={isEnglish} /> {copy(isEnglish, '管理情報', 'Administrative data')}
        <Badge imp="aux" isEnglish={isEnglish} /> {copy(isEnglish, '補足情報', 'Auxiliary data')}
      </div>
    </div>
  </Window>
);

/* =====================================================
   STEP 3 — Adjust importance & save as template (no data)
   ===================================================== */
const StageTemplate = ({ isEnglish }) => (
  <Window title={copy(isEnglish, 'PITHIAS Extract — テンプレート保存', 'PITHIAS Extract — Save template')}>
    <div className="hiw-split">
      <div className="hiw-pane">
        <div className="hiw-pane-label">{copy(isEnglish, '重要度を調整', 'Adjust importance')}</div>
        <div className="hiw-table">
          <div className="hiw-row hiw-row--3 hiw-static">
            <span className="hiw-cell hiw-cell--path">{copy(isEnglish, '請求先', 'Bill to')}</span>
            <span className="hiw-cell hiw-cell--val">{copy(isEnglish, '株式会社ABC商事', 'ABC Trading Co., Ltd.')}</span>
            <span className="hiw-cell hiw-cell--imp"><Badge imp="core" isEnglish={isEnglish} /></span>
          </div>
          {/* This row visibly changes aux -> admin */}
          <div className="hiw-row hiw-row--3 hiw-static hiw-row--edit">
            <span className="hiw-cell hiw-cell--path">{copy(isEnglish, '発行日', 'Issue date')}</span>
            <span className="hiw-cell hiw-cell--val">{copy(isEnglish, '2024年9月5日', 'September 5, 2024')}</span>
            <span className="hiw-cell hiw-cell--imp hiw-swap">
              <Badge imp="aux" className="hiw-swap-out" isEnglish={isEnglish} />
              <Badge imp="admin" className="hiw-swap-in" isEnglish={isEnglish} />
            </span>
          </div>
          <div className="hiw-row hiw-row--3 hiw-static">
            <span className="hiw-cell hiw-cell--path">{copy(isEnglish, '合計金額', 'Total')}</span>
            <span className="hiw-cell hiw-cell--val">¥330,000</span>
            <span className="hiw-cell hiw-cell--imp"><Badge imp="core" isEnglish={isEnglish} /></span>
          </div>
        </div>

        <div className="hiw-save-row">
          <span className="hiw-input hiw-input--type">{copy(isEnglish, '請求書_標準フォーマット', 'Invoice_standard_format')}</span>
          <button className="hiw-btn hiw-btn--save hiw-press hiw-press--late">
            {copy(isEnglish, 'テンプレート保存', 'Save template')}
            <span className="hiw-cursor hiw-cursor--late" />
          </button>
        </div>
      </div>

      <div className="hiw-pane">
        <div className="hiw-pane-label">{copy(isEnglish, '保存されるテンプレート', 'Saved template')}</div>
        <div className="hiw-template-card hiw-template-card--in">
          <div className="hiw-template-name">📁 {copy(isEnglish, '請求書_標準フォーマット', 'Invoice_standard_format')}</div>
          {['タイトル', '請求書番号', '発行日', '請求先', '品目 / 名称', '合計金額'].map((c, i) => (
            <div key={c} className="hiw-template-col" style={{ '--i': i }}>
              <span className="hiw-cell--path">{isEnglish ? FIELD_EN[c] || c : c}</span>
              <span className="hiw-masked">{copy(isEnglish, '— 実データは保存されません —', '— Source data is not saved —')}</span>
            </div>
          ))}
          <div className="hiw-zero-badge">🔒 {copy(isEnglish, 'ゼロリテンション（カラム名のみ保存）', 'Zero retention (column names only)')}</div>
        </div>
      </div>
    </div>
  </Window>
);

/* =====================================================
   STEP 4 — Re-extract with template & importance filter
   ===================================================== */
const FILTERED = ROWS.filter((r) => r.imp === 'core');

const StageReuse = ({ isEnglish }) => (
  <Window title={copy(isEnglish, 'PITHIAS Extract — テンプレートで再抽出', 'PITHIAS Extract — Extract with template')}>
    <div className="hiw-single">
      <div className="hiw-reuse-controls">
        <label className="hiw-select">
          {copy(isEnglish, 'テンプレート：', 'Template: ')}<strong>{copy(isEnglish, '請求書_標準フォーマット', 'Invoice_standard_format')} ▾</strong>
        </label>
        <div className="hiw-filter-chips">
          <span className="hiw-filter hiw-filter--on">{copy(isEnglish, 'コア', 'Core')}</span>
          <span className="hiw-filter">{copy(isEnglish, '管理用', 'Administrative')}</span>
          <span className="hiw-filter">{copy(isEnglish, '補助用', 'Auxiliary')}</span>
        </div>
        <button className="hiw-btn hiw-btn--extract hiw-press">
          {copy(isEnglish, '抽出を実行', 'Run extraction')}
          <span className="hiw-cursor" />
        </button>
      </div>

      <div className="hiw-table">
        <div className="hiw-thead hiw-thead--3">
          <span>{copy(isEnglish, '項目名', 'Field')}</span>
          <span>{copy(isEnglish, '抽出された値', 'Extracted value')}</span>
          <span>{copy(isEnglish, '重要度', 'Importance')}</span>
        </div>
        {ROWS.map((r, i) => {
          const keep = r.imp === 'core';
          return (
            <div
              key={r.path}
              className={`hiw-row hiw-row--3 ${keep ? 'hiw-row--keep' : 'hiw-row--drop'}`}
              style={{ '--i': i }}
            >
              <span className="hiw-cell hiw-cell--path">{isEnglish ? FIELD_EN[r.path] || r.path : r.path}</span>
              <span className="hiw-cell hiw-cell--val">{isEnglish ? VALUE_EN[r.value] || r.value : r.value}</span>
              <span className="hiw-cell hiw-cell--imp"><Badge imp={r.imp} isEnglish={isEnglish} /></span>
            </div>
          );
        })}
      </div>
      <div className="hiw-count hiw-count--in">
        {copy(isEnglish, `「コア」のみ ${FILTERED.length} 項目を抽出（管理用・補助用は除外）✓`, `${FILTERED.length} Core fields extracted (Administrative and Auxiliary excluded) ✓`)}
      </div>
    </div>
  </Window>
);

const STAGES = [StageUpload, StageImportance, StageTemplate, StageReuse];

/* =====================================================
   Main component
   ===================================================== */
class HowItWorksDemo extends React.Component {
  state = { active: 0, paused: false };

  componentDidMount() {
    this.startTimer();
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  startTimer = () => {
    this.stopTimer();
    this.timer = setInterval(() => {
      if (!this.state.paused) {
        this.setState((s) => ({ active: (s.active + 1) % STEPS.length }));
      }
    }, AUTO_ADVANCE_MS);
  };

  stopTimer = () => {
    if (this.timer) clearInterval(this.timer);
  };

  goTo = (i) => {
    this.setState({ active: i });
    this.startTimer(); // reset the countdown on manual selection
  };

  render() {
    const { isEnglish } = this.props;
    const { active, paused } = this.state;
    const Stage = STAGES[active];

    return (
      <div
        className="hiw"
        onMouseEnter={() => this.setState({ paused: true })}
        onMouseLeave={() => this.setState({ paused: false })}
      >
        {/* Step navigation */}
        <div className="hiw-nav">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              type="button"
              className={`hiw-nav-item ${i === active ? 'is-active' : ''}`}
              onClick={() => this.goTo(i)}
              aria-label={isEnglish ? s.labelEn : s.label}
            >
              <span className="hiw-nav-num">{`0${i + 1}`}</span>
              <Image src={s.icon} alt={isEnglish ? s.labelEn : s.label} width={56} height={56} />
              <span className="hiw-nav-label">{isEnglish ? s.labelEn : s.label}</span>
              {i === active && !paused && (
                <span key={`p-${active}`} className="hiw-nav-progress" />
              )}
            </button>
          ))}
        </div>

        {/* Caption for the active step */}
        <p className="hiw-caption">{isEnglish ? STEPS[active].captionEn : STEPS[active].caption}</p>

        {/* Animated stage — key forces the enter animations to replay */}
        <div className="hiw-stage" key={active}>
          <Stage isEnglish={isEnglish} />
        </div>
      </div>
    );
  }
}

export default withLanguage(HowItWorksDemo);
